import styled, { css } from 'styled-components';

import { PopupMenu } from '@polyai/ui/components/molecules/PopupMenu';
import type { ColorName } from '@polyai/ui/styles/config/colors';
import themeColors, { baseColors } from '@polyai/ui/styles/config/colors';
import themeSpacing from '@polyai/ui/styles/config/spacing';

export const TAG_BOX_MAX_WIDTH = 260;

export const ActionTagContainer = styled.span`
  padding: ${themeSpacing.xxs2};
  white-space: nowrap;
`;

type TagBoxWrapperProps = {
  $open?: boolean;
  $iconOnly?: boolean;
  $disabled?: boolean;
  $loading?: boolean;
  $backgroundColor?: string;
  $borderColor?: ColorName;
  $isStatic?: boolean;
  $isSelected?: boolean;
};

const tagBoxWrapperHoverStyles = css<TagBoxWrapperProps>`
  ${({ $borderColor }) => css`
    border-color: ${
      $borderColor ? themeColors[$borderColor] : baseColors.brand['50']
    };
    outline: none;
  `};
`;

export const TagBoxWrapper = styled.span<TagBoxWrapperProps>`
  cursor: pointer;
  display: inline-flex;
  height: 100%;
  ${({ $backgroundColor }) => css`
    background-color: ${
      $backgroundColor ? $backgroundColor : themeColors.backgroundPage
    };
    border: solid 1px
      ${$backgroundColor ? $backgroundColor : themeColors.backgroundPage};
  `}
  border-radius: ${themeSpacing.radiusXSmall};

  svg {
    margin-bottom: ${themeSpacing.xxs1};
    vertical-align: middle;
  }

  ${({ $disabled, $loading }) =>
    ($disabled || $loading) &&
    css`
      border-color: ${themeColors.textDisabled};
    `};

  ${({ $disabled }) =>
    $disabled &&
    css`
      padding-right: ${themeSpacing.xs1};
    `};

  ${({ $iconOnly, $disabled, $open, $borderColor }) =>
    !$iconOnly &&
    !$disabled &&
    css`
      &:focus,
      &:hover {
        ${tagBoxWrapperHoverStyles};
      }

      ${
        $open &&
        css`
        border-color: ${
          $borderColor ? themeColors[$borderColor] : baseColors.brand['50']
        };
      `
      }
    `};

  ${({ $isSelected }) => $isSelected && tagBoxWrapperHoverStyles};

  ${({ $isStatic }) =>
    $isStatic &&
    css`
      cursor: default;
      background: ${themeColors.backgroundPage};
      border: none;

      &:hover {
        border: none;
      }
    `};
`;

export const TagBox = styled.span<{
  $iconOnly?: boolean;
  $disabled?: boolean;
  $isOverflow?: boolean;
  $backgroundColor?: string;
  $isStatic?: boolean;
}>`
  cursor: pointer;

  display: inline-block;

  margin: 0.5px;
  padding: 0 ${themeSpacing.xxs2};

  svg {
    display: inline-block;
  }

  ${({ $isOverflow }) =>
    $isOverflow &&
    css`
      overflow-x: hidden;
      width: ${TAG_BOX_MAX_WIDTH}px;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}

  ${({ $backgroundColor }) => css`
    background-color: ${
      $backgroundColor ? $backgroundColor : themeColors.backgroundPage
    };
    border: solid 1px
      ${$backgroundColor ? $backgroundColor : themeColors.backgroundPage};
  `}
    border-radius: ${themeSpacing.radiusXSmall};

  ${({ $iconOnly, $disabled }) =>
    !$iconOnly &&
    !$disabled &&
    css`
      div:first-child svg {
        margin-right: ${themeSpacing.xs1};
      }

      div:last-child svg {
        margin-left: ${themeSpacing.xs1};
      }
    `}

  ${({ $isStatic }) =>
    $isStatic &&
    css`
      cursor: inherit;
      background-color: ${themeColors.backgroundPage};
    `};
`;

export const InlinePopup = styled(PopupMenu)`
  display: inline-block;
`;

export const ActionName = styled.span<{ $color?: ColorName }>`
  user-select: all;

  ${({ $color }) =>
    $color &&
    css`
      color: ${themeColors[$color]};
    `}
`;
